import { useState } from "react";
import { C } from "../../../constants";
import { Ic } from "../../../components/ui/Icons";
import { inputStyle, inputFocus, inputBlur } from "../../../components/ui/Components";
import FileUpload from "../../../components/ui/FileUpload";

function Label({ en, es, required }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.t1 }}>{en}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}</span>
      <span style={{ display: "block", fontSize: 11.5, color: C.t3, fontStyle: "italic" }}>{es}</span>
    </div>
  );
}

function Section({ title, titleEs }) {
  return (
    <div style={{ borderBottom: `2px solid ${C.navy}15`, paddingBottom: 8, marginTop: 32, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h3>
      {titleEs && <span style={{ fontSize: 12, color: C.t3, fontStyle: "italic" }}>{titleEs}</span>}
    </div>
  );
}

function TextField({ en, es, value, onChange, placeholder, type = "text", required, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en={en} es={es} required={required} />
      {type === "textarea" ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={4} placeholder={placeholder} style={{ ...inputStyle, resize: "vertical", minHeight: 100, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...inputStyle, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />
      )}
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}
    </div>
  );
}

export default function OtherDetectionForm({ user, onSubmit, onBack, onComplete }) {
  const [form, setForm] = useState({
    projectName: "", projectLocation: "", endClient: "",
    estimatedClosingDate: "", estimatedExecDate: "",
    additionalNotes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [files, setFiles] = useState([]);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const validate = () => {
    const e = {};
    const req = (key, msg) => { if (!form[key] || !form[key].trim()) e[key] = msg || "Required"; };
    req("projectName", "Project name is required");
    req("projectLocation", "Project location is required");
    req("endClient", "End client is required");
    req("estimatedClosingDate", "Closing date is required");
    req("estimatedExecDate", "Execution date is required");
    req("additionalNotes", "Please describe your requirements");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      onSubmit({ ...form, solutionType: "Design Request: Detection - Other", submittedBy: user.name }, files);
      setSubmitting(false);
      setShowSuccess(true);
    }, 800);
  };

  if (showSuccess) {
    return (
      <div style={{ maxWidth: 500, margin: "80px auto", textAlign: "center" }}>
        <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "48px 40px", boxShadow: "0 8px 40px rgba(0,0,0,0.06)" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>✓</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Request Submitted Successfully</h2>
          <p style={{ fontSize: 14, color: C.t2, margin: "0 0 4px", lineHeight: 1.5 }}>Your detection request has been received and is now pending review.</p>
          <p style={{ fontSize: 13, color: C.t3, fontStyle: "italic", margin: "0 0 28px" }}>Su solicitud de detección ha sido recibida y está pendiente de revisión.</p>
          <button onClick={onComplete || onBack}
            style={{ padding: "12px 36px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", boxShadow: `0 4px 16px ${C.navy}25` }}>
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", padding: "4px 0", marginBottom: 16, transition: "color 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.color = C.navy} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
        <Ic.ChevronLeft /> Back to Detection Systems
      </button>

      <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "32px 36px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#64748B12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📋</div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: 0 }}>Other Detection Request</h2>
            <p style={{ fontSize: 13, color: C.t2, margin: 0 }}>Custom requirements</p>
          </div>
        </div>
        <p style={{ fontSize: 12, color: C.t3, fontStyle: "italic", margin: "0 0 4px", lineHeight: 1.5 }}>
          Please provide your project details and describe your requirements in the notes section.<br />
          Por favor proporcione los detalles del proyecto y describa sus requisitos en la sección de notas.
        </p>
        <p style={{ fontSize: 11, color: C.t3, margin: "0 0 24px" }}><span style={{ color: C.red }}>*</span> Required fields / Campos obligatorios</p>

        <Section title="Project Information" titleEs="Información del Proyecto" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TextField en="Project Name" es="Nombre del Proyecto" value={form.projectName} onChange={v => set("projectName", v)} placeholder="e.g. Custom Detection Project" required error={errors.projectName} />
          <TextField en="Project Location" es="Ubicación del Proyecto" value={form.projectLocation} onChange={v => set("projectLocation", v)} placeholder="City, Country" required error={errors.projectLocation} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Label en="End Client" es="Cliente Final" required />
          <input value={form.endClient} onChange={e => set("endClient", e.target.value)} placeholder="Final client name" style={{ ...inputStyle, borderColor: errors.endClient ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />
          <p style={{ fontSize: 11, color: C.t3, fontStyle: "italic", margin: "4px 0 0" }}>Confidential — only used for project registration with the manufacturer.<br />Confidencial — solo se usa para el registro del proyecto con el fabricante.</p>
          {errors.endClient && <span style={{ fontSize: 11, color: C.red }}>{errors.endClient}</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TextField en="Estimated Closing Date" es="Fecha Estimada de Cierre" value={form.estimatedClosingDate} onChange={v => set("estimatedClosingDate", v)} type="date" required error={errors.estimatedClosingDate} />
          <TextField en="Estimated Execution Date" es="Fecha Estimada de Ejecución" value={form.estimatedExecDate} onChange={v => set("estimatedExecDate", v)} type="date" required error={errors.estimatedExecDate} />
        </div>

        <Section title="Requirements Description" titleEs="Descripción de Requisitos" />
        <TextField en="Notes / Comments" es="Notas / Comentarios" value={form.additionalNotes} onChange={v => set("additionalNotes", v)} type="textarea" placeholder="Please describe your detection requirements, system type, area details, and any other relevant information..." required error={errors.additionalNotes} />

        <Section title="Reference Documents" titleEs="Documentos de Referencia" />
        <FileUpload files={files} onChange={setFiles} />

        {Object.keys(errors).length > 0 && (
          <div style={{ marginTop: 24, padding: "12px 16px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <p style={{ fontSize: 13, color: C.red, fontWeight: 600, margin: "0 0 4px" }}>Please complete all required fields before submitting.</p>
            <p style={{ fontSize: 12, color: C.red, margin: 0, fontStyle: "italic" }}>Por favor complete todos los campos obligatorios antes de enviar.</p>
          </div>
        )}

        <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onBack} style={{ padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting}
            style={{ padding: "12px 32px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 15, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: `0 4px 16px ${C.navy}25`, opacity: submitting ? 0.75 : 1 }}>
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
